import React, { useState } from "react";
import { Check, ChevronRight, X } from "lucide-react";
import { cn } from "../../lib/utils";

const DISMISSED_KEY = "khronos_checklist_dismissed";

const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * "Get the most out of Khronos": a short list that ticks itself off as you
 * use the app. Gone once everything's done, or when dismissed.
 *
 * items: [{ id, title, hint, done, onClick }]
 */
const StartChecklist = ({ items }) => {
  const [dismissed, setDismissed] = useState(readDismissed);
  const remaining = items.filter((i) => !i.done).length;
  if (dismissed || remaining === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* storage disabled: it may come back next visit */
    }
  };

  return (
    <section aria-label="Get started" className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold text-muted-foreground">
          Get the most out of Khronos
          <span className="ml-1.5 font-normal text-muted-foreground/70">
            {items.length - remaining} of {items.length} done
          </span>
        </h4>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide this list"
          className="rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={item.onClick}
            className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)_1rem] items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
          >
            <span
              className={cn(
                "grid h-5 w-5 place-items-center rounded-full",
                item.done
                  ? "bg-secondary text-white"
                  : "border-[1.5px] border-dashed border-muted-foreground/60",
              )}
            >
              {item.done && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  "block truncate text-sm font-medium",
                  item.done ? "text-muted-foreground line-through" : "text-white",
                )}
              >
                {item.title}
              </span>
              {!item.done && item.hint && (
                <span className="mt-px block truncate text-xs text-muted-foreground">
                  {item.hint}
                </span>
              )}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
          </button>
        ))}
      </div>
    </section>
  );
};

export default StartChecklist;
